import { TYPES } from '@Types';
import { inject, injectable } from 'inversify';
import { ApiClient, type user_dto, user_preference } from '@Api';
import {
  RequestAction,
  RequestFailure,
  RequestSuccess,
} from '@Repository/RequestAction';
import {
  RequestLocationSuccess,
  RequestUserPreferenceSuccess,
} from './response';
import { Locale } from '@Repository/locale';
import { UserPreferenceMapper } from '@Mapper/user/UserPreferenceMapper';
import { Nullable, UserPreference } from '@Models';
import { CACHE_NAME } from '@Worker/CacheManager';

@injectable()
export class UserPreferenceRepository {
  @inject(TYPES.Locale) private _locale!: Locale;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;
  @inject(TYPES.Mapper.UserPreferenceMapper)
  private _userPreferenceMapper!: UserPreferenceMapper;

  public async get(): Promise<RequestAction> {
    try {
      const preferenceDto =
        await this._apiClient.user.getCurrentUserPreferences({
          locale: this._locale.getlocale(),
        });
      this.setCachedEntity(preferenceDto);
      return new RequestUserPreferenceSuccess({
        payload: this._userPreferenceMapper.map(preferenceDto),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  public async getCached(): Promise<Nullable<UserPreference>> {
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(`/user/user_preference`);
      if (!response) return await null;

      const json: user_preference = JSON.parse(await response.text());

      return this._userPreferenceMapper.map(json) ?? (await null);
    } catch (err) {
      console.log('error-load-cache', err);
    }
    return null;
  }

  async saveLocation(playerId: string, location: google.maps.LatLngLiteral) {
    try {
      await this._apiClient.user.updateUser({
        locale: this._locale.getlocale(),
        uuid: playerId,
        requestBody: {
          id: playerId,
          latitude: location.lat,
          longitude: location.lng,
        } satisfies user_dto as user_dto,
      });

      return new RequestLocationSuccess({
        payload: location,
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  async deleteLocation(playerId: string) {
    try {
      await this._apiClient.user.updateUser({
        locale: this._locale.getlocale(),
        uuid: playerId,
        requestBody: {
          id: playerId,
          latitude: null,
          longitude: null,
        } satisfies user_dto as user_dto,
      });

      return new RequestSuccess();
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  protected async setCachedEntity(data: user_preference): Promise<void> {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(
        `/user/user_preference`,
        new Response(JSON.stringify(data)),
      );
    } catch (err) {
      console.log('error-save-cached-entity', err);
    }
  }
}
